/**
 * Profile sheet (docs/11 §3.2, docs/10 S1): the questions that used to be a
 * sign-up wall. Asked once after the first Home load — the driver has seen
 * the app behind it — and always skippable. Also the "Profile" editor from
 * Settings, where Save is the only exit that persists.
 */
import { useState } from 'react';
import { api, ApiError } from './api.js';
import { Sheet, Button, Chip, InlineWarn } from './ui.js';

const AGE_BANDS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'] as const;
const USE_CASES: [string, string][] = [
  ['full_time_driver', 'Full-time'], ['part_time_driver', 'Part-time'],
  ['occasional', 'Now and then'], ['exploring', 'Just looking'],
];
const GENDERS: [string, string][] = [['female', 'Female'], ['male', 'Male'], ['nonbinary', 'Non-binary']];

export interface ProfileValues {
  display_name: string | null; age_band: string | null; use_case: string | null; gender: string | null;
}

export function ProfileSheet({ mode, initial, onSaved, onClose }: {
  mode: 'first' | 'edit';
  initial: ProfileValues;
  onSaved: (v: ProfileValues) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial.display_name ?? '');
  const [age, setAge] = useState(initial.age_band);
  const [useCase, setUseCase] = useState(initial.use_case);
  const [gender, setGender] = useState(initial.gender);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.updateMe({ display_name: name.trim() || (initial.display_name ?? 'Driver'), age_band: age, use_case: useCase, gender });
      onSaved({ display_name: name.trim() || initial.display_name, age_band: age, use_case: useCase, gender });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t save — check your signal.');
      setBusy(false);
    }
  }

  return (
    <Sheet title={mode === 'first' ? 'Two quick questions' : 'Profile'} onClose={onClose} labelledBy="profile-title">
      {mode === 'first' && (
        <p className="sub">They help us understand who Surge serves. Nothing here is shown to anyone or sold.</p>
      )}
      {mode === 'edit' && (
        <div className="field">
          <label htmlFor="pname">Name</label>
          <input id="pname" type="text" autoComplete="name" value={name} maxLength={50} onChange={(e) => setName(e.target.value)} />
        </div>
      )}
      <p className="section">Age</p>
      <div className="chips">
        {AGE_BANDS.map((b) => <Chip key={b} selected={age === b} onClick={() => setAge(b)}>{b}</Chip>)}
      </div>
      <p className="section">How you drive</p>
      <div className="chips">
        {USE_CASES.map(([v, label]) => <Chip key={v} selected={useCase === v} onClick={() => setUseCase(v)}>{label}</Chip>)}
      </div>
      <p className="section">Gender · optional</p>
      <div className="chips">
        {GENDERS.map(([v, label]) => (
          <Chip key={v} selected={gender === v} onClick={() => setGender(gender === v ? null : v)}>{label}</Chip>
        ))}
        <Chip selected={gender === null} onClick={() => setGender(null)}>Prefer not to say</Chip>
      </div>
      {error && <InlineWarn danger>{error}</InlineWarn>}
      <Button variant="primary" busy={busy} onClick={() => void save()}>{mode === 'first' ? 'Done' : 'Save'}</Button>
      {mode === 'first' && <Button variant="quiet" onClick={onClose}>Not now</Button>}
    </Sheet>
  );
}
